import { useEffect, useState } from "react";
import {
  DLUGOSCI,
  DOMYSLNA_DLUGOSC,
  DOMYSLNA_MAX_RANGA,
  type Dlugosc,
  ETYKIETY_DLUGOSCI,
  GAMES,
  OPISY_DLUGOSCI,
  OPISY_RANGI,
  PROGI_RANGI,
  gameInfo,
} from "@gierki/shared";
import { api } from "../api.ts";
import { useStore } from "../store.ts";
import { Ekran, Karta, Komunikat, Odznaka, Pole, Przycisk } from "../ui.tsx";

/**
 * Zakładanie partii: kogo zapraszamy i na jakich zasadach gramy.
 *
 * Kategorie i liczba rund są tu, a nie w osobnym ekranie ustawień, bo to
 * jedyny moment, w którym ktokolwiek chce je zmieniać — a przewijanie do nich
 * później, w jadącym aucie, nikomu nie służy.
 */

const KATEGORIE_STANDARD = [
  { key: "panstwo", label: "Państwo" },
  { key: "miasto", label: "Miasto" },
  { key: "imie", label: "Imię" },
  { key: "zwierze", label: "Zwierzę" },
  { key: "roslina", label: "Roślina" },
  { key: "rzecz", label: "Rzecz" },
  { key: "rzeka", label: "Rzeka" },
  { key: "gora", label: "Góra" },
  { key: "kolor", label: "Kolor" },
  { key: "owoc-warzywo", label: "Owoc lub warzywo" },
];

/**
 * Domyślna piątka zostaje bez zmian mimo dziesięciu kategorii do wyboru.
 *
 * Limit to dziesięć kategorii na partię, ale runda z dziesięcioma polami trwa
 * dwa razy dłużej i kończy się tym, że połowa jest pusta. Nowe kategorie mają
 * być do wyboru, nie do wypełniania za każdym razem.
 */
const DOMYSLNE = ["panstwo", "miasto", "imie", "zwierze", "roslina"];

export function NowaPartia({ onWroc }: { onWroc: () => void }) {
  const { user, createGame, showToast } = useStore();
  const [gra, setGra] = useState<string>(GAMES[0]?.id ?? "panstwa-miasta");
  const [konta, setKonta] = useState<Array<{ id: string; nick: string }>>([]);
  const [zaproszeni, setZaproszeni] = useState<string[]>([]);
  const [kategorie, setKategorie] = useState<string[]>(DOMYSLNE);
  const [wlasna, setWlasna] = useState("");
  const [rundy, setRundy] = useState(8);
  const [sekundy, setSekundy] = useState(180);
  const [turaSekundy, setTuraSekundy] = useState(0);
  const [pary, setPary] = useState(true);
  // Superfarmer — domyślne wartości zgodne z instrukcją z pudełka.
  const [lisJeden, setLisJeden] = useState(true);
  const [wilkKroliki, setWilkKroliki] = useState(false);
  const [wymianaP2P, setWymianaP2P] = useState(true);
  const [graDalej, setGraDalej] = useState(false);
  const [limitStada, setLimitStada] = useState(true);
  const [wieleWymian, setWieleWymian] = useState(false);
  // Perudo — domyślne wartości zgodne z instrukcją z pudełka.
  const [kosciNaStart, setKosciNaStart] = useState(5);
  const [odkrycieSekundy, setOdkrycieSekundy] = useState(8);
  const [jedynkiDzikie, setJedynkiDzikie] = useState(true);
  const [calzaEnabled, setCalzaEnabled] = useState(true);
  const [calzaOdzyskuje, setCalzaOdzyskuje] = useState(true);
  const [palificoEnabled, setPalificoEnabled] = useState(true);
  const [palificoRaz, setPalificoRaz] = useState(true);
  const [zaczynaPrzegrany, setZaczynaPrzegrany] = useState(true);
  // Statki — flota reguluje długość partii, plansza jest zawsze 10×10.
  const [flotaKrotka, setFlotaKrotka] = useState(false);
  const [statkiStykac, setStatkiStykac] = useState(false);
  const [strzalZaTrafienie, setStrzalZaTrafienie] = useState(true);
  const [maxStrzalow, setMaxStrzalow] = useState(3);
  const [salwaSekundy, setSalwaSekundy] = useState(30);
  const [rozstawianieSekundy, setRozstawianieSekundy] = useState(120);
  const [pewnaWoda, setPewnaWoda] = useState(true);
  /*
   * Makao — dziewiętnaście ustawień w jednym obiekcie, nie w dziewiętnastu
   * `useState`. To jedyna gra z tej piątki, w której **każdy dom gra inaczej**,
   * więc wszystko sporne jest przełącznikiem; wartości domyślne to wersja
   * najpowszechniejsza, ta sama co `default` w schemacie na serwerze.
   */
  const [makao, setMakao] = useState({
    kartNaStart: 5,
    dwojkiTrojki: true,
    czworkaPostoj: true,
    postojKumuluje: true,
    obronaBezKoloru: true,
    kroleBojowe: true,
    krolKierWTyl: true,
    krolKontraDwojka: false,
    waletZada: true,
    zadanieNaOkrazenie: true,
    asZmieniaKolor: true,
    damaNaWszystko: false,
    ostatniaKartaFunkcyjna: true,
    dobieranieDoSkutku: false,
    karaZaBrakMakao: 5,
    makaoSeconds: 10,
    zadanieSeconds: 20,
    graDoOstatniego: true,
  });
  const mk = <K extends keyof typeof makao>(klucz: K, wartosc: (typeof makao)[K]) =>
    setMakao((biezace) => ({ ...biezace, [klucz]: wartosc }));
  // Literaki — `jezyk` pierwszy, bo zmienia znaczenie wszystkiego pod spodem.
  const [jezyk, setJezyk] = useState<"pl" | "en" | "es">("pl");

  /*
   * Wordle — `jezyk` dzieli z Literakami, bo znaczy w obu dokładnie to samo
   * i naraz zakłada się jedną partię. `czesciMowy` jest **wielokrotnym
   * wyborem**, a pusta lista znaczy „wszystkie": ta sama wartość wychodzi ze
   * starej partii bez tego pola i z lobby, w którym host odznaczył wszystko.
   */
  const [czesciMowy, setCzesciMowy] = useState<string[]>([]);
  /*
   * Długość hasła: **trzy przyciski, nie suwak**. Suwak o trzech stopniach jest
   * gorszy od przycisków pod każdym względem — trudniej trafić, nie widać
   * wszystkich opcji naraz i trzeba czytać opis, żeby wiedzieć, gdzie się stoi.
   * Suwak wygrał przy `maxRanga` dlatego, że progów jest osiem.
   */
  const [dlugosc, setDlugosc] = useState<Dlugosc>(DOMYSLNA_DLUGOSC);
  /*
   * Próg popularności haseł trzymamy jako **indeks w `PROGI_RANGI`**, nie jako
   * rangę: suwak ma osiem równych stopni, a same wartości rosną nierówno
   * (5 000 … 500 000). Gdyby suwak chodził po liczbie, dziewięć dziesiątych jego
   * długości przypadałoby na trzy najwyższe progi.
   */
  const [maxRangaIndeks, setMaxRangaIndeks] = useState(
    Math.max(0, PROGI_RANGI.indexOf(DOMYSLNA_MAX_RANGA)),
  );
  const maxRanga = PROGI_RANGI[maxRangaIndeks] ?? DOMYSLNA_MAX_RANGA;
  /*
   * Pisownia: **ustawienie, nie przełącznik trudności**. Wyłączone znaczy „do
   * gry wchodzą tylko słowa bez ogonków", a nie „ogonki się nie liczą" —
   * ukrytej równości `ł = l` w tej grze nie ma. Domyślnie wyłączone, bo tak
   * zachowuje się partia zapisana przed dodaniem tego pola.
   */
  const [ogonki, setOgonki] = useState(true);
  const [wordleRundy, setWordleRundy] = useState(5);
  const [wordleSekundy, setWordleSekundy] = useState(300);
  const [probyNaRunde, setProbyNaRunde] = useState(6);
  const [wordlePostep, setWordlePostep] = useState(true);
  const [bonusKolejnosci, setBonusKolejnosci] = useState(true);
  const [literakiRundy, setLiterakiRundy] = useState(5);
  const [literakiSekundy, setLiterakiSekundy] = useState(120);
  const [liczbaLiter, setLiczbaLiter] = useState(9);
  const [minDlugosc, setMinDlugosc] = useState(3);
  const [duplikatProcent, setDuplikatProcent] = useState(50);
  const [zKodem, setZKodem] = useState(false);
  const [trwa, setTrwa] = useState(false);

  const wybrana = gameInfo(gra);

  /**
   * Gra dla jednej osoby — Kości i Wordle.
   *
   * Bez tego przycisk „Załóż partię" był wyłączony, dopóki kogoś nie zaprosiłeś
   * albo nie włączyłeś kodu pokoju: warunek pilnował, żeby nie zakładać pustej
   * partii, ale nie znał gier, w które da się grać samemu. Serwer takiej partii
   * nigdy nie blokował — sprawdza wyłącznie `maxPlayers`.
   */
  const samotna = (wybrana?.minGraczy ?? 2) <= 1;

  /*
   * Limit tury zależy od gry, a nie od gustu zakładającego.
   *
   * Zero („bez limitu") jest w porządku tam, gdzie własna tura nikogo nie
   * blokuje — w Kościach i Superfarmerze. W Perudzie tura blokuje cały stół, bo
   * runda nie może się skończyć bez decyzji jednej osoby: gra, w której jeden
   * telefon zatrzymuje pozostałe, nie ma prawa startować bez limitu.
   */
  const DOMYSLNY_LIMIT: Record<string, number> = { perudo: 45, makao: 60 };
  useEffect(() => {
    setTuraSekundy(DOMYSLNY_LIMIT[gra] ?? 0);
    // Świadomie bez `DOMYSLNY_LIMIT` w zależnościach: to stała tablicy, a nie stan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gra]);

  useEffect(() => {
    api
      .users()
      .then(({ users }) => setKonta(users.filter((entry) => entry.id !== user?.id)))
      .catch(() => undefined);
  }, [user?.id]);

  const przelacz = (lista: string[], wartosc: string): string[] =>
    lista.includes(wartosc) ? lista.filter((item) => item !== wartosc) : [...lista, wartosc];

  /** Każda gra ma inny kształt ustawień — serwer i tak je waliduje. */
  const ustawienia = (): unknown =>
    gra === "panstwa-miasta"
      ? { categories: kategorie, roundCount: rundy, roundSeconds: sekundy }
      : gra === "kosci"
        ? { turnSeconds: turaSekundy, paryEnabled: pary }
        : gra === "superfarmer"
          ? {
              turnSeconds: turaSekundy,
              lisZostawiaJednego: lisJeden,
              wilkZjadaKroliki: wilkKroliki,
              wymianaMiedzyGraczami: wymianaP2P,
              graDalejPoZwyciezcy: graDalej,
              limitStada,
              wieleWymian,
            }
          : gra === "perudo"
            ? {
                turnSeconds: turaSekundy,
                odkrycieSeconds: odkrycieSekundy,
                kosciNaStart,
                jedynkiDzikie,
                calzaEnabled,
                calzaOdzyskujeKosc: calzaOdzyskuje,
                palificoEnabled,
                palificoRazNaGracza: palificoRaz,
                zaczynaPrzegrany,
              }
            : gra === "statki"
              ? {
                  flota: flotaKrotka ? "krotka" : "klasyczna",
                  statkiMogaSieStykac: statkiStykac,
                  strzalZaTrafienie,
                  maxStrzalowWSalwie: maxStrzalow,
                  salwaSeconds: salwaSekundy,
                  rozstawianieSeconds: rozstawianieSekundy,
                  pokazujPewnaWode: pewnaWoda,
                }
              : gra === "literaki"
                ? {
                    jezyk,
                    roundCount: literakiRundy,
                    roundSeconds: literakiSekundy,
                    liczbaLiter,
                    minDlugosc,
                    duplikatProcent,
                  }
                : gra === "wordle"
                ? {
                    jezyk,
                    dlugosc,
                    ogonki,
                    czesciMowy,
                    maxRanga,
                    roundCount: wordleRundy,
                    roundSeconds: wordleSekundy,
                    probyNaRunde,
                    pokazujPostep: wordlePostep,
                    bonusKolejnosci,
                  }
                : gra === "makao"
                ? { ...makao, turnSeconds: turaSekundy }
                : { turnSeconds: turaSekundy };

  const zaloz = async () => {
    setTrwa(true);
    try {
      await createGame({
        gameType: gra,
        settings: ustawienia(),
        invitedUserIds: zaproszeni,
        withCode: zKodem,
      });
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Nie udało się założyć partii.");
      setTrwa(false);
    }
  };

  const dodajWlasna = () => {
    const wartosc = wlasna.trim();
    if (wartosc && !kategorie.includes(wartosc)) setKategorie([...kategorie, wartosc]);
    setWlasna("");
  };

  return (
    <Ekran
      tytul="Nowa partia"
      podtytul={wybrana?.nazwa}
      akcja={
        <Przycisk wariant="cichy" onClick={onWroc}>
          Anuluj
        </Przycisk>
      }
      stopka={
        <Przycisk
          wariant="glowny"
          pelna
          onClick={() => void zaloz()}
          disabled={
            trwa ||
            (!samotna && zaproszeni.length === 0 && !zKodem) ||
            (gra === "panstwa-miasta" && kategorie.length === 0)
          }
        >
          {trwa ? "Zakładam…" : "Załóż partię"}
        </Przycisk>
      }
    >
      <Sekcja tytul="W co gramy">
        <div className="space-y-2">
          {GAMES.map((info) => (
            <Karta key={info.id} onClick={() => setGra(info.id)}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className={gra === info.id ? "text-akcent font-semibold" : ""}>
                    {info.nazwa}
                  </div>
                  <div className="text-sm text-tekst-slaby">{info.opis}</div>
                </div>
                <Odznaka aktywna={gra === info.id}>
                  {info.minGraczy === info.maxGraczy
                    ? `${info.minGraczy} os.`
                    : `${info.minGraczy}–${info.maxGraczy}`}
                </Odznaka>
              </div>
            </Karta>
          ))}
        </div>
      </Sekcja>

      <Sekcja tytul="Kto gra">
        {konta.length === 0 ? (
          <Komunikat>
            Nie ma jeszcze innych kont. Zaproś kogoś linkiem albo zaznacz kod pokoju poniżej.
          </Komunikat>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {konta.map((konto) => (
            <Przycisk
              key={konto.id}
              wariant={zaproszeni.includes(konto.id) ? "glowny" : "zwykly"}
              onClick={() => setZaproszeni(przelacz(zaproszeni, konto.id))}
            >
              {konto.nick}
            </Przycisk>
          ))}
        </div>
        <Karta onClick={() => setZKodem(!zKodem)}>
          <div className="flex items-center justify-between">
            <div>
              <div>Kod pokoju dla gościa</div>
              <div className="text-sm text-tekst-slaby">
                Ktoś bez konta dołączy czterema cyframi
              </div>
            </div>
            <Odznaka aktywna={zKodem}>{zKodem ? "TAK" : "NIE"}</Odznaka>
          </div>
        </Karta>
      </Sekcja>

      {gra !== "panstwa-miasta" ? null : (
      <Sekcja tytul="Kategorie">
        <div className="flex flex-wrap gap-2">
          {KATEGORIE_STANDARD.map((kategoria) => (
            <Przycisk
              key={kategoria.key}
              wariant={kategorie.includes(kategoria.key) ? "glowny" : "zwykly"}
              onClick={() => setKategorie(przelacz(kategorie, kategoria.key))}
            >
              {kategoria.label}
            </Przycisk>
          ))}
          {kategorie
            .filter((kategoria) => !KATEGORIE_STANDARD.some((item) => item.key === kategoria))
            .map((kategoria) => (
              <Przycisk
                key={kategoria}
                wariant="glowny"
                onClick={() => setKategorie(przelacz(kategorie, kategoria))}
              >
                {kategoria}
              </Przycisk>
            ))}
        </div>
        <div className="flex gap-2">
          <Pole
            value={wlasna}
            onChange={(event) => setWlasna(event.target.value)}
            placeholder="Własna kategoria, np. marka auta"
            enterKeyHint="done"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                dodajWlasna();
              }
            }}
          />
          <Przycisk onClick={dodajWlasna} disabled={!wlasna.trim()}>
            Dodaj
          </Przycisk>
        </div>
        <div className="text-sm text-tekst-slaby">
          Wszystkie kategorie z listy mają słownik — trafienie zatwierdza się samo. Własna kategoria
          i odpowiedzi spoza słownika idą pod ocenę graczy.
        </div>
      </Sekcja>
      )}

      {gra === "panstwa-miasta" ? (
        <Sekcja tytul="Ile gramy">
          <Suwak
            etykieta="Rund"
            wartosc={rundy}
            min={1}
            max={20}
            krok={1}
            onChange={setRundy}
            opis={`${rundy} rund`}
          />
          <Suwak
            etykieta="Czas rundy"
            wartosc={sekundy}
            min={30}
            max={600}
            krok={30}
            onChange={setSekundy}
            opis={`${Math.floor(sekundy / 60)}:${String(sekundy % 60).padStart(2, "0")}`}
          />
        </Sekcja>
      ) : gra === "statki" ? (
        /*
         * Statki mają dwa okna czasu zamiast tury, więc suwak „limit na turę"
         * byłby tu napisem bez pokrycia. Salwa **nie ma** wariantu „bez
         * limitu": jest wspólna, więc jeden zamyślony telefon zatrzymuje trzy
         * pozostałe — to ten przypadek, w którym Catan stawia deadline.
         */
        <Sekcja tytul="Tempo i reguły">
          <Suwak
            etykieta="Czas salwy"
            wartosc={salwaSekundy}
            min={10}
            max={120}
            krok={5}
            onChange={setSalwaSekundy}
            opis={`${salwaSekundy} s`}
          />
          <Suwak
            etykieta="Czas na rozstawienie"
            wartosc={rozstawianieSekundy}
            min={30}
            max={600}
            krok={30}
            onChange={setRozstawianieSekundy}
            opis={`${Math.floor(rozstawianieSekundy / 60)}:${String(rozstawianieSekundy % 60).padStart(2, "0")}`}
          />
          <div className="text-sm text-tekst-slaby">
            Kto nie zdąży rozstawić floty, dostanie ją od serwera — rozstawienie jest czynnością
            mechaniczną, więc serwer po prostu naciska przycisk „Rozstaw losowo".
          </div>

          <Przelacznik
            etykieta="Flota krótka"
            opis="Czteromasztowiec, trójmasztowiec, dwa dwumasztowce i dwa jednomasztowce — 13 pól zamiast 20. Krótszy przejazd, te same reguły."
            wartosc={flotaKrotka}
            onChange={setFlotaKrotka}
          />
          <Przelacznik
            etykieta="Dodatkowy strzał za trafienie"
            opis="Bonus wchodzi w salwie następnej, nie natychmiast — inaczej szczęściarz strzelałby pięć razy, a reszta patrzyła."
            wartosc={strzalZaTrafienie}
            onChange={setStrzalZaTrafienie}
          />
          {strzalZaTrafienie ? (
            <Suwak
              etykieta="Najwięcej strzałów w salwie"
              wartosc={maxStrzalow}
              min={1}
              max={5}
              krok={1}
              onChange={setMaxStrzalow}
              opis={`${maxStrzalow}`}
            />
          ) : null}
          <Przelacznik
            etykieta="Statki mogą się stykać"
            opis="Domyślnie nie. Przy stykaniu znika obrys wodą wokół zatopionych — przestaje być prawdą, bo cztery trafienia w linii nie muszą już być jednym statkiem."
            wartosc={statkiStykac}
            onChange={setStatkiStykac}
          />
          <Przelacznik
            etykieta="Zakrywaj wodę wokół zatopionych"
            opis="Oszczędza kilkanaście spalonych strzałów na partię. Bez znaczenia, gdy statki mogą się stykać."
            wartosc={pewnaWoda && !statkiStykac}
            onChange={setPewnaWoda}
          />
        </Sekcja>
      ) : gra === "literaki" ? (
        <Sekcja tytul="Język i tempo">
          {/*
            * Język stoi pierwszy, bo zmienia wiadro liter i sędziego — czyli
            * znaczenie wszystkiego pod spodem. Trzy przyciski, nie lista
            * rozwijana: wybór z trzech pozycji nie jest wart dwóch dotknięć.
            * Po starcie języka **nie da się zmienić**; zestaw z wiadra
            * polskiego oceniany słownikiem hiszpańskim byłby wyjątkowo brzydki.
            */}
          <div className="flex gap-2">
            {([
              ["pl", "Polski"],
              ["en", "Angielski"],
              ["es", "Hiszpański"],
            ] as const).map(([kod, etykieta]) => (
              <Przycisk
                key={kod}
                wariant={jezyk === kod ? "glowny" : "zwykly"}
                onClick={() => setJezyk(kod)}
              >
                {etykieta}
              </Przycisk>
            ))}
          </div>

          <Suwak
            etykieta="Rund"
            wartosc={literakiRundy}
            min={1}
            max={12}
            krok={1}
            onChange={setLiterakiRundy}
            opis={`${literakiRundy}`}
          />
          <Suwak
            etykieta="Czas rundy"
            wartosc={literakiSekundy}
            min={30}
            max={300}
            krok={15}
            onChange={setLiterakiSekundy}
            opis={`${Math.floor(literakiSekundy / 60)}:${String(literakiSekundy % 60).padStart(2, "0")}`}
          />
          <Suwak
            etykieta="Liter w zestawie"
            wartosc={liczbaLiter}
            min={7}
            max={10}
            krok={1}
            onChange={setLiczbaLiter}
            opis={`${liczbaLiter}`}
          />
          <Suwak
            etykieta="Najkrótsze słowo"
            wartosc={minDlugosc}
            min={2}
            max={5}
            krok={1}
            onChange={setMinDlugosc}
            opis={`${minDlugosc} litery`}
          />
          <Suwak
            etykieta="Za słowo, które ktoś też miał"
            wartosc={duplikatProcent}
            min={0}
            max={100}
            krok={25}
            onChange={setDuplikatProcent}
            opis={`${duplikatProcent}%`}
          />
          <div className="text-sm text-tekst-slaby">
            Sto procent wyłącza karę za powtórkę — nikt nie traci za to, że wpadł na to samo. Zero
            to wariant „liczy się tylko unikalne": sprawdzony w rodzinie, brutalny.
          </div>
        </Sekcja>
      ) : gra === "wordle" ? (
        <Sekcja tytul="Język, słowa i tempo">
          {/*
            * Język stoi pierwszy, bo zmienia znaczenie wszystkiego pod spodem:
            * inny alfabet, inna klawiatura, inna lista haseł. Trzy przyciski,
            * nie lista rozwijana — wybór ma być widoczny bez otwierania.
            * Po starcie języka **nie da się zmienić**: hasło wylosowane po
            * polsku zostałoby w stanie, a klawiatura przestawiłaby się na
            * hiszpańską i przestałaby mieć klawisze, którymi da się je wpisać.
            */}
          <div className="flex gap-2">
            {([
              ["pl", "Polski"],
              ["en", "Angielski"],
              ["es", "Hiszpański"],
            ] as const).map(([kod, etykieta]) => (
              <Przycisk
                key={kod}
                wariant={jezyk === kod ? "glowny" : "zwykly"}
                onClick={() => setJezyk(kod)}
              >
                {etykieta}
              </Przycisk>
            ))}
          </div>

          {/*
            * Długość hasła stoi zaraz pod językiem, bo razem z nim wybiera
            * **listę**, z której losuje się hasło: `data/wordle/pl/4/` to inny
            * plik niż `data/wordle/pl/5/`, a nie ten sam z filtrem.
            *
            * Trzy zwykłe przyciski w rzędzie: na 390 px z marginesami zostaje
            * 358 px, więc trzy cele po ~115 px — grubo powyżej progu 56 px,
            * przez który odpadły ogonki. Zmiany po starcie nie ma z tego samego
            * powodu, co przy języku: hasło zostałoby w stanie, a siatka
            * przestawiłaby liczbę kafelków.
            */}
          <div className="flex gap-2">
            {DLUGOSCI.map((ile) => (
              <Przycisk
                key={ile}
                wariant={dlugosc === ile ? "glowny" : "zwykly"}
                onClick={() => setDlugosc(ile)}
              >
                {ETYKIETY_DLUGOSCI[ile]}
              </Przycisk>
            ))}
          </div>
          <div className="text-sm text-tekst-slaby">
            {OPISY_DLUGOSCI[dlugosc]}. Czteroliterowych haseł jest w każdym języku
            wyraźnie mniej niż pięcioliterowych — przy wąskim filtrze części mowy
            serwer potrafi takiej partii nie pozwolić założyć.
          </div>

          {/*
            * Pisownia stoi zaraz pod długością, bo razem z nią i z językiem
            * wybiera **listę słów**, a nie sposób porównywania: partia bez
            * ogonków nie zamienia `ł` na `l`, tylko wpuszcza wyłącznie słowa,
            * które `ł` nie mają. Dla angielskiego przełącznik nic nie zmienia
            * i mówi to wprost, zamiast znikać — znikający przełącznik czyta się
            * jak usterka.
            */}
          <Przelacznik
            etykieta="Ogonki"
            opis={
              jezyk === "en"
                ? "Angielski ogonków nie ma — to ustawienie nic tu nie zmienia."
                : "Włączone: hasłem może być `jeśli` czy `żółty`, a klawiatura dostaje klawisz dokładający ogonek do ostatniej litery. Wyłączone: do gry wchodzą tylko słowa bez ogonków — pula jest wtedy wyraźnie węższa, a polskie czasowniki znikają prawie zupełnie (kończą się na `-ć`)."
            }
            wartosc={ogonki}
            onChange={setOgonki}
          />

          {/*
            * Części mowy stoją zaraz pod językiem, bo pula haseł zależy od
            * obu naraz. Nic nie zaznaczone znaczy **wszystkie** — i tak ma
            * być: „pusta lista = nic nie wolno wylosować" dałoby partię
            * wywracającą się przy pierwszej rundzie na sytuacji, którą host
            * wyklikał w dwie sekundy.
            *
            * Licznika „hasła do wylosowania: N" tutaj nie ma, bo przeglądarka
            * nie widzi list haseł — pokazuje go lobby założonej partii.
            * Kombinację poniżej progu odrzuca serwer w `parseSettings`,
            * komunikatem, który mówi wprost, ile haseł zostało i co z tym
            * zrobić.
            */}
          <div className="flex flex-wrap gap-2">
            {([
              ["subst", "Rzeczowniki"],
              ["verb", "Czasowniki"],
              ["adj", "Przymiotniki"],
              ["adv", "Przysłówki"],
              ["inne", "Inne"],
            ] as const).map(([kod, etykieta]) => (
              <Przycisk
                key={kod}
                wariant={czesciMowy.includes(kod) ? "glowny" : "zwykly"}
                onClick={() => setCzesciMowy(przelacz(czesciMowy, kod))}
              >
                {etykieta}
              </Przycisk>
            ))}
          </div>
          <div className="text-sm text-tekst-slaby">
            {czesciMowy.length === 0
              ? "Nic nie zaznaczone znaczy wszystkie części mowy."
              : "Hasło będzie z zaznaczonych klas. Jedna klasa bywa za wąska — wtedy dołóż drugą."}
          </div>

          {/*
            * Popularność haseł: **suwak, nie osiem przycisków**.
            *
            * Osiem celów po 56 px nie zmieści się na 390 px w dwóch rzędach bez
            * zejścia poniżej progu trafialności, a rząd przewijany w bok chowa
            * połowę opcji przed kimś, kto nie wie, że są. Suwak ma jeden cel
            * dotykowy na całą szerokość i pokazuje skalę wprost: w lewo łatwiej,
            * w prawo trudniej. Liczba stoi obok nazwy, bo po rundzie ekran
            * wyników pokazuje rangę hasła i te dwie liczby mają się dać
            * porównać.
            */}
          <Suwak
            etykieta="Popularność haseł"
            wartosc={maxRangaIndeks}
            min={0}
            max={PROGI_RANGI.length - 1}
            krok={1}
            onChange={setMaxRangaIndeks}
            opis={`${OPISY_RANGI[maxRanga] ?? ""} (${maxRanga.toLocaleString("pl")})`}
          />
          <div className="text-sm text-tekst-slaby">
            Im dalej w prawo, tym rzadsze słowa mogą paść jako hasło. Dwa pierwsze
            stopnie to słownictwo z lekcji — mają sens po angielsku i hiszpańsku,
            po polsku bywają za ciasne i serwer wtedy nie pozwoli założyć partii.
          </div>

          <Suwak
            etykieta="Rund"
            wartosc={wordleRundy}
            min={1}
            max={10}
            krok={1}
            onChange={setWordleRundy}
            opis={`${wordleRundy}`}
          />
          <Suwak
            etykieta="Czas rundy"
            wartosc={wordleSekundy}
            min={60}
            max={900}
            krok={30}
            onChange={setWordleSekundy}
            opis={`${Math.floor(wordleSekundy / 60)}:${String(wordleSekundy % 60).padStart(2, "0")}`}
          />
          <Suwak
            etykieta="Prób na rundę"
            wartosc={probyNaRunde}
            min={3}
            max={8}
            krok={1}
            onChange={setProbyNaRunde}
            opis={`${probyNaRunde}`}
          />
          <Przelacznik
            etykieta="Pasek postępu"
            opis="Widać, ile prób kto zużył. Kolorów cudzych kafelków nie pokazuje żadne ustawienie."
            wartosc={wordlePostep}
            onChange={setWordlePostep}
          />
          <Przelacznik
            etykieta="Bonus za kolejność"
            opis="+5 / +3 / +1 dla trzech pierwszych trafień. Wyłączony zostawia remisy remisami."
            wartosc={bonusKolejnosci}
            onChange={setBonusKolejnosci}
          />
        </Sekcja>
      ) : gra === "makao" ? (
        /*
         * Makao ma dziewiętnaście ustawień i to jest jego cecha, nie wada:
         * każdy dom gra inaczej, a przełącznik istnieje po to, żeby nie kłócić
         * się przy stole. Pogrupowane, bo ściana dziewiętnastu przełączników
         * bez podziału jest sama w sobie barierą wejścia.
         */
        <>
          <Sekcja tytul="Rozdanie i tempo">
            <Suwak
              etykieta="Kart na start"
              wartosc={makao.kartNaStart}
              min={3}
              max={7}
              krok={1}
              onChange={(v) => mk("kartNaStart", v)}
              opis={`${makao.kartNaStart}`}
            />
            <Suwak
              etykieta="Limit na turę"
              wartosc={turaSekundy}
              min={0}
              max={300}
              krok={15}
              onChange={setTuraSekundy}
              opis={turaSekundy === 0 ? "bez limitu" : `${turaSekundy} s`}
            />
            <div className="text-sm text-tekst-slaby">
              Tura w Makale blokuje cały stół — nikt inny nie może zagrać ani dobrać. Zero zostawcie
              na partię rozgrywaną na postoju.
            </div>
            <Suwak
              etykieta="Czas na żądanie"
              wartosc={makao.zadanieSeconds}
              min={5}
              max={120}
              krok={5}
              onChange={(v) => mk("zadanieSeconds", v)}
              opis={`${makao.zadanieSeconds} s`}
            />
            <Przelacznik
              etykieta="Gramy do przedostatniego"
              opis="Partia trwa, aż zostanie jeden z kartami. Wyłączone — kończy się na pierwszym wychodzącym."
              wartosc={makao.graDoOstatniego}
              onChange={(v) => mk("graDoOstatniego", v)}
            />
            <Przelacznik
              etykieta="Dobieranie do skutku"
              opis="Brak ruchu = dobieraj, aż będzie czym zagrać. Domyślnie jedna karta i pas."
              wartosc={makao.dobieranieDoSkutku}
              onChange={(v) => mk("dobieranieDoSkutku", v)}
            />
          </Sekcja>

          <Sekcja tytul="Karty funkcyjne">
            <Przelacznik
              etykieta="Dwójka i trójka karzą"
              opis="+2 i +3 karty dla następnego, z kumulacją. Wyłączone — zwykłe karty."
              wartosc={makao.dwojkiTrojki}
              onChange={(v) => mk("dwojkiTrojki", v)}
            />
            <Przelacznik
              etykieta="Obrona bez koloru"
              opis="Broniąca dwójka czy trójka nie musi pasować kolorem. Wyłączone czyni obronę loterią przy trzech kartach na ręce."
              wartosc={makao.obronaBezKoloru}
              onChange={(v) => mk("obronaBezKoloru", v)}
            />
            <Przelacznik
              etykieta="Czwórka zabiera kolejkę"
              opis="Postój dla następnego, do odbicia własną czwórką."
              wartosc={makao.czworkaPostoj}
              onChange={(v) => mk("czworkaPostoj", v)}
            />
            <Przelacznik
              etykieta="Postoje się kumulują"
              opis="Czwórka na czwórkę przekazuje cały stos plus jeden. Wyłączone — zawsze jedna kolejka."
              wartosc={makao.postojKumuluje}
              onChange={(v) => mk("postojKumuluje", v)}
            />
            <Przelacznik
              etykieta="Króle bojowe"
              opis="Król pik i kier dokładają pięć kart. Wyłączone — wszystkie króle zwykłe."
              wartosc={makao.kroleBojowe}
              onChange={(v) => mk("kroleBojowe", v)}
            />
            <Przelacznik
              etykieta="Król kier bije w tył"
              opis="Uderza poprzedniego gracza, a odbicie królem pik zawraca karę. Wyłączone — kier działa jak pik."
              wartosc={makao.krolKierWTyl}
              onChange={(v) => mk("krolKierWTyl", v)}
            />
            <Przelacznik
              etykieta="Król kontra dwójka"
              opis="Łączy oba łańcuchy kar. Domyślnie wyłączone: mieszanie ich daje kary po dwadzieścia kart i partie, w których nikt nie wychodzi."
              wartosc={makao.krolKontraDwojka}
              onChange={(v) => mk("krolKontraDwojka", v)}
            />
            <Przelacznik
              etykieta="Walet żąda figury"
              opis="Żądać wolno wyłącznie figur 5–10: żądanie waletów zapętla grę, a asów zamienia ją w loterię kolorów."
              wartosc={makao.waletZada}
              onChange={(v) => mk("waletZada", v)}
            />
            <Przelacznik
              etykieta="Żądanie na jedno okrążenie"
              opis="Wygasa, gdy tura wróci do autora waleta. Wyłączone — trwa do zagrania żądanej figury, co przy trzech graczach potrafi zablokować stół na pół talii."
              wartosc={makao.zadanieNaOkrazenie}
              onChange={(v) => mk("zadanieNaOkrazenie", v)}
            />
            <Przelacznik
              etykieta="As zmienia kolor"
              opis="Kolor obowiązuje do skutku, kolejny as zmienia go ponownie."
              wartosc={makao.asZmieniaKolor}
              onChange={(v) => mk("asZmieniaKolor", v)}
            />
            <Przelacznik
              etykieta="Dama na wszystko"
              opis="Wariant „dama na wszystko, wszystko na damę”. Domyślnie wyłączony: włączony czyni damę najsilniejszą kartą w talii."
              wartosc={makao.damaNaWszystko}
              onChange={(v) => mk("damaNaWszystko", v)}
            />
            <Przelacznik
              etykieta="Wolno wyjść kartą funkcyjną"
              opis="Wychodzisz królem pik — następny dobiera pięć. Wyjście asem albo waletem i tak nie tworzy żądania: nie ma komu go wybrać."
              wartosc={makao.ostatniaKartaFunkcyjna}
              onChange={(v) => mk("ostatniaKartaFunkcyjna", v)}
            />
          </Sekcja>

          <Sekcja tytul="Makao">
            <Suwak
              etykieta="Kara za brak makao"
              wartosc={makao.karaZaBrakMakao}
              min={0}
              max={10}
              krok={1}
              onChange={(v) => mk("karaZaBrakMakao", v)}
              opis={makao.karaZaBrakMakao === 0 ? "bez kary" : `${makao.karaZaBrakMakao} kart`}
            />
            <Suwak
              etykieta="Okno na „Makao”"
              wartosc={makao.makaoSeconds}
              min={3}
              max={30}
              krok={1}
              onChange={(v) => mk("makaoSeconds", v)}
              opis={`${makao.makaoSeconds} s`}
            />
            <div className="text-sm text-tekst-slaby">
              W aucie nikt nie usłyszy, kto krzyknął pierwszy, więc „makao” jest przyciskiem
              z oknem czasowym. Okno nie zatrzymuje gry, a jeśli wygaśnie bez reakcji — nic się nie
              dzieje. Zero w karze wyłącza całą regułę razem z oknem.
            </div>
          </Sekcja>
        </>
      ) : (
        <Sekcja tytul="Tempo">
          <Suwak
            etykieta="Limit na turę"
            wartosc={turaSekundy}
            min={0}
            max={300}
            krok={15}
            onChange={setTuraSekundy}
            opis={turaSekundy === 0 ? "bez limitu" : `${turaSekundy} s`}
          />
          <div className="text-sm text-tekst-slaby">
            Bez limitu partia czeka na gracza dowolnie długo — można ją dokończyć następnego dnia.
            Powiadomienie przypomni, że wypadła Twoja tura.
          </div>

          {/*
            * Para i dwie pary tylko w Kościach. Przełącznik, a nie stała:
            * arkusz rośnie z trzynastu kratek do piętnastu, więc partia
            * rozegrana na jednych regułach nie porównuje się z drugą.
            */}
          {gra === "kosci" && (
            <Przelacznik
              etykieta="Para i dwie pary"
              opis="Dwie dodatkowe kategorie, po sumie wszystkich kości — arkusz ma wtedy piętnaście rund zamiast trzynastu."
              wartosc={pary}
              onChange={setPary}
            />
          )}

          {/*
            * Superfarmer: reguły, które każdy dom gra inaczej.
            *
            * Domyślne wartości są przepisane z instrukcji z pudełka — przełącznik
            * istnieje po to, żeby nie kłócić się przy stole, a nie żeby zgadywać,
            * jak było naprawdę.
            */}
          {gra === "superfarmer" && (
            <>
              <Przelacznik
                etykieta="Lis zostawia jednego królika"
                opis="Tak stoi w instrukcji. Wyłączone — lis zabiera wszystkie."
                wartosc={lisJeden}
                onChange={setLisJeden}
              />
              <Przelacznik
                etykieta="Wilk zjada też króliki"
                opis="Instrukcja: nie. Koń i króliki są przed wilkiem bezpieczne."
                wartosc={wilkKroliki}
                onChange={setWilkKroliki}
              />
              <Przelacznik
                etykieta="Wymiana między graczami"
                opis="Propozycja i akceptacja, po kursach z tabeli. Bez tego wymieniacie się tylko ze stadem głównym."
                wartosc={wymianaP2P}
                onChange={setWymianaP2P}
              />
              <Przelacznik
                etykieta="Gra dalej po pierwszym Superfarmerze"
                opis="Pozostali grają o kolejne miejsca. Domyślnie partia kończy się na pierwszym komplecie."
                wartosc={graDalej}
                onChange={setGraDalej}
              />
              <Przelacznik
                etykieta="Wiele wymian w turze"
                opis="Instrukcja daje jedną wymianę przed rzutem. Bez limitu da się w jednej turze przejść tabelę od królików aż po konia."
                wartosc={wieleWymian}
                onChange={setWieleWymian}
              />
              <Przelacznik
                etykieta="Skończone stado główne"
                opis="60 królików, 24 owce, 20 świń, 12 krów, 6 koni. Wyłączone — pudełko jest bez dna."
                wartosc={limitStada}
                onChange={setLimitStada}
              />
            </>
          )}

          {/*
            * Perudo: wszystko, co różne domy grają inaczej.
            *
            * Domyślne wartości są przepisane z instrukcji z pudełka. Kości na
            * start skracają albo wydłużają partię bardziej niż cokolwiek innego
            * — trójka mieści się w przejeździe na zakupy.
            */}
          {gra === "perudo" && (
            <>
              <Suwak
                etykieta="Kości w kubku"
                wartosc={kosciNaStart}
                min={1}
                max={6}
                krok={1}
                onChange={setKosciNaStart}
                opis={`${kosciNaStart}`}
              />
              <Suwak
                etykieta="Pokaz kubków"
                wartosc={odkrycieSekundy}
                min={3}
                max={60}
                krok={1}
                onChange={setOdkrycieSekundy}
                opis={`${odkrycieSekundy} s`}
              />
              <div className="text-sm text-tekst-slaby">
                Po każdym „kłamiesz” kubki idą w górę na tyle sekund. To jedyny moment, w którym
                gra pokazuje karty — za krótko i nikt nie zdąży policzyć.
              </div>
              <Przelacznik
                etykieta="Jedynki dzikie"
                opis="Jedynka liczy się jako każde oczko i ma własne kursy przejścia. Bez tego zostaje zwykły liar's dice."
                wartosc={jedynkiDzikie}
                onChange={setJedynkiDzikie}
              />
              <Przelacznik
                etykieta="„Dokładnie tyle”"
                opis="Wolno zawołać calzę. Jedyny ruch w tej grze, który daje coś z powrotem."
                wartosc={calzaEnabled}
                onChange={setCalzaEnabled}
              />
              <Przelacznik
                etykieta="Trafiona calza oddaje kość"
                opis="Do pełnego kubka. Wyłączone — trafiona calza tylko kończy rundę bez straty."
                wartosc={calzaOdzyskuje}
                onChange={setCalzaOdzyskuje}
              />
              <Przelacznik
                etykieta="Palifico"
                opis="Po zejściu gracza do jednej kości: jedynki nie są dzikie, oczko ustala otwierający, podbija się wyłącznie ilością."
                wartosc={palificoEnabled}
                onChange={setPalificoEnabled}
              />
              <Przelacznik
                etykieta="Palifico raz na gracza"
                opis="Bez tego gracz z jedną kością wymuszałby palifico co drugą rundę."
                wartosc={palificoRaz}
                onChange={setPalificoRaz}
              />
              <Przelacznik
                etykieta="Rundę otwiera przegrany"
                opis="Reguła z pudełka: zaczyna ten, kogo dotknął wynik. Wyłączone — kolejny w kolejności siedzenia."
                wartosc={zaczynaPrzegrany}
                onChange={setZaczynaPrzegrany}
              />
            </>
          )}
        </Sekcja>
      )}
    </Ekran>
  );
}

/** Przełącznik reguły: nazwa, zdanie wyjaśnienia i checkbox wielkości kciuka. */
function Przelacznik({
  etykieta,
  opis,
  wartosc,
  onChange,
}: {
  etykieta: string;
  opis: string;
  wartosc: boolean;
  onChange: (wartosc: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 py-2">
      <span>
        <span className="block">{etykieta}</span>
        <span className="block text-sm text-tekst-slaby">{opis}</span>
      </span>
      <input
        type="checkbox"
        checked={wartosc}
        onChange={(event) => onChange(event.target.checked)}
        className="h-6 w-6 accent-akcent"
      />
    </label>
  );
}

function Sekcja({ tytul, children }: { tytul: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm uppercase tracking-wide text-tekst-slaby">{tytul}</h2>
      {children}
    </section>
  );
}

function Suwak({
  etykieta,
  wartosc,
  min,
  max,
  krok,
  opis,
  onChange,
}: {
  etykieta: string;
  wartosc: number;
  min: number;
  max: number;
  krok: number;
  opis: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex justify-between">
        <span>{etykieta}</span>
        <span className="tabular text-tekst-slaby">{opis}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={krok}
        value={wartosc}
        onChange={(event) => onChange(Number(event.target.value))}
        className="w-full h-12 accent-[var(--color-akcent)]"
      />
    </div>
  );
}
